import { useState, useCallback, useEffect } from 'react'
import { Shield } from 'lucide-react'
import { handleValidationErrors, calculateAgeAtIncident } from '@/shared/utils'
import { validateRobloxExploitationDetails } from '../validation/robloxExploitationDetailsValidation'
import { US_STATES } from '@/features/enrollment/core/utils/usStates'
import type { RobloxExploitationDetailsProps, ValidationErrors } from '@/features/enrollment'
import ValidationErrorDisplay from '../../shared/ValidationErrorDisplay'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'

export default function Step2_ExploitationDetails({
  enrollmentData,
  updateEnrollmentData,
  updateStepData,
  handleNavigate,
  handleSimpleNavigate,
  handleNavigateWithCompletion,
  isNavigating,
  isCompleting,
  currentStepIndex,
  totalSteps = 10
}: RobloxExploitationDetailsProps) {
  const [errors, setErrors] = useState<ValidationErrors>({})
  
  // ✅ Real-time validation on date selection - shows DQ errors immediately
  useEffect(() => {
    if (!enrollmentData.exploitationStartDate) {
      // Clear date errors if date is cleared
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.exploitationStartDate
        return newErrors
      })
      return
    }

    const today = new Date()
    const incidentDate = new Date(enrollmentData.exploitationStartDate)
    
    if (isNaN(incidentDate.getTime())) {
      return
    }

    const threeWeeksAgo = new Date(today.getTime() - (3 * 7 * 24 * 60 * 60 * 1000))
    const twoYearsAgo = new Date(today.getTime() - (2 * 365 * 24 * 60 * 60 * 1000))
    
    // Check date range and set errors immediately
    if (incidentDate > threeWeeksAgo) {
      setErrors(prev => ({
        ...prev,
        exploitationStartDate: 'DQ: Event happened too recently - exploitation must have started at least 3 weeks ago'
      }))
    } else if (incidentDate < twoYearsAgo) {
      setErrors(prev => ({
        ...prev,
        exploitationStartDate: 'DQ: Event happened too long ago - exploitation must have started within the last 2 years'
      }))
    } else if (incidentDate > today) {
      setErrors(prev => ({
        ...prev,
        exploitationStartDate: 'DQ: Exploitation start date cannot be in the future'
      }))
    } else {
      // Date is valid - clear the error
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.exploitationStartDate
        return newErrors
      })
    }
  }, [enrollmentData.exploitationStartDate])
  
  // Auto-calculate age at exploitation when date of birth and exploitation start date are available
  // This automatically populates the age field when the incident date changes
  useEffect(() => {
    if (enrollmentData.injuredPartyDOB && enrollmentData.exploitationStartDate) {
      const calculatedAge = calculateAgeAtIncident(
        enrollmentData.injuredPartyDOB,
        enrollmentData.exploitationStartDate
      )
      
      // Always update to ensure it reflects the current date selection
      // Only update if calculation is valid (not null, not negative)
      if (calculatedAge !== null && calculatedAge >= 0) {
        updateEnrollmentData('injuredPartyAgeAtExploitation', calculatedAge)
      } else {
        // Clear age field if calculation is invalid (incident before birth)
        updateEnrollmentData('injuredPartyAgeAtExploitation', null)
      }
    } else {
      // Clear age field if required data is missing
      updateEnrollmentData('injuredPartyAgeAtExploitation', null)
    }
  }, [enrollmentData.injuredPartyDOB, enrollmentData.exploitationStartDate, updateEnrollmentData])

  // Helper function to update step data
  const updateField = useCallback((field: string, value: any) => {
    updateEnrollmentData(field, value)
  }, [updateEnrollmentData])

  const handleValidation = useCallback(() => {
    const validationErrors = validateRobloxExploitationDetails(enrollmentData)
    setErrors(validationErrors)
    return Object.keys(validationErrors).length === 0
  }, [enrollmentData])

  const handleNext = useCallback(async (): Promise<boolean> => {
    const validationErrors = validateRobloxExploitationDetails(enrollmentData)
    setErrors(validationErrors)

    const isValid = Object.keys(validationErrors).length === 0

    if (!isValid) {
      // Scroll to first error field and highlight all errors
      handleValidationErrors(validationErrors)
      return false
    }

    return true
  }, [enrollmentData])

  const getValidationErrors = (): string[] => {
    return Object.values(errors).filter(Boolean)
  }

  const handleBack = useCallback(() => {
    // Clear any validation errors when going back
    setErrors({})
  }, [])

  const handleCheckboxChange = useCallback((field: string, value: string, checked: boolean) => {
    const currentValues = enrollmentData[field] || []
    let newValues: string[]
    
    if (checked) {
      newValues = [...currentValues, value]
    } else {
      newValues = currentValues.filter((item: string) => item !== value)
    }
    
    updateField(field, newValues)
  }, [enrollmentData, updateField])

  const exploitationTypes = [
    'Sexual exploitation (includes sexual assault)',
    'Sextortion',
    'None of the Above'
  ]

  const communicationTypes = [
    'Sexualized message',
    'Sent or received sexual image through another app',
    'Coerced the minor to meet in person and sexually assaulted them',
    'Offered Robux (in-game currency), gift cards, or money in exchange for sexual images',
    'Offered Robux (in-game currency), gift cards, or money in exchange for an in person meeting resulting in sexual assault',
    'Coerced the minor to move to another platform (e.g., Snapchat, Discord) in exchange for money/Robux/gift cards etc...',
    'None of the above'
  ]

  return (
    <StageWrapper
      currentStepIndex={currentStepIndex ?? 0}
      totalSteps={totalSteps}
      handleNavigate={handleNavigate}
      handleNavigateWithCompletion={handleNavigateWithCompletion}
      onNext={handleNext}
      onBack={handleBack}
      isNavigating={isNavigating}
      isCompleting={isCompleting}
    >
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
            <Shield className="w-6 h-6 text-secondary-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-accent-900">Roblox Exploitation Details</h3>
            <p className="text-sm text-accent-600">Provide specific details about the incident.</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Exploitation Type */}
          <div>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Did the injured party experience any of the following through Roblox? *
              </label>
              {exploitationTypes.map((type) => (
                <label key={type} className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={(enrollmentData.exploitationTypes || []).includes(type)}
                    onChange={(e) => handleCheckboxChange('exploitationTypes', type, e.target.checked)}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{type}</span>
                </label>
              ))}
              {errors.exploitationTypes && (
                <p className="mt-2 text-sm text-red-600">{errors.exploitationTypes}</p>
              )}
              
              {/* DQ Warning when "None of the Above" is selected */}
              {(enrollmentData.exploitationTypes || []).includes('None of the Above') && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ DISQUALIFICATION WARNING: Selecting "None of the Above" will disqualify this enrollment.
                  </p>
                </div>
              )}
              
              <p className="mt-2 text-xs text-red-600">
                IF None of the Above - DQ
              </p>
            </div>
          </div>

          {/* Other Injuries */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Does the injured party have any other injuries or diagnoses? *
            </label>
            <select
              value={enrollmentData.hasOtherInjuries === undefined ? '' : enrollmentData.hasOtherInjuries ? 'Yes' : 'No'}
              onChange={(e) => updateField('hasOtherInjuries', e.target.value === 'Yes')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.hasOtherInjuries ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select an option</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            {errors.hasOtherInjuries && (
              <p className="mt-1 text-sm text-red-600">{errors.hasOtherInjuries}</p>
            )}
          </div>

          {/* Location Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What state does the injured party live in? *
              </label>
              <select
                value={enrollmentData.injuredPartyResidentState || ''}
                onChange={(e) => updateField('injuredPartyResidentState', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.injuredPartyResidentState ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {US_STATES.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
              {errors.injuredPartyResidentState && (
                <p className="mt-1 text-sm text-red-600">{errors.injuredPartyResidentState}</p>
              )}
              
              {/* DQ Warning when Florida is selected */}
              {enrollmentData.injuredPartyResidentState?.toLowerCase() === 'florida' && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ DISQUALIFICATION WARNING: Florida residents are not eligible for this campaign.
                  </p>
                </div>
              )}
              
              <p className="mt-1 text-xs text-red-600">
                IF FLORIDA - DQ
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Which state did the injury occur in? *
              </label>
              <select
                value={enrollmentData.injuryOccurredState || ''}
                onChange={(e) => updateField('injuryOccurredState', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.injuryOccurredState ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {US_STATES.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
              {errors.injuryOccurredState && (
                <p className="mt-1 text-sm text-red-600">{errors.injuryOccurredState}</p>
              )}
              
              {/* DQ Warning when Florida is selected */}
              {enrollmentData.injuryOccurredState?.toLowerCase() === 'florida' && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ DISQUALIFICATION WARNING: Injuries that occurred in Florida are not eligible for this campaign.
                  </p>
                </div>
              )}
              
              <p className="mt-1 text-xs text-red-600">
                IF FLORIDA - DQ
              </p>
            </div>
          </div>

          {/* Timeline Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Approximately, when did the sexual exploitation start? *
              </label>
              <input
                type="date"
                value={enrollmentData.exploitationStartDate || ''}
                onChange={(e) => updateField('exploitationStartDate', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.exploitationStartDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.exploitationStartDate && (
                <p className="mt-1 text-sm text-red-600">{errors.exploitationStartDate}</p>
              )}
              
              {/* DQ Warning when date is too recent (less than 3 weeks ago) */}
              {errors.exploitationStartDate && errors.exploitationStartDate.includes('too recently') && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ DISQUALIFICATION WARNING: The exploitation start date must be at least 3 weeks ago.
                  </p>
                </div>
              )}
              
              {/* DQ Warning when date is too long ago (more than 2 years ago) */}
              {errors.exploitationStartDate && errors.exploitationStartDate.includes('too long ago') && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ DISQUALIFICATION WARNING: The exploitation must have started within the last 2 years to be eligible for this campaign.
                  </p>
                </div>
              )}
              
              {/* DQ Warning when date is in the future */}
              {errors.exploitationStartDate && errors.exploitationStartDate.includes('future') && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ DISQUALIFICATION WARNING: The exploitation start date cannot be in the future.
                  </p>
                </div>
              )}
              
              {!errors.exploitationStartDate && (
                <p className="mt-1 text-xs text-blue-600">
                  Must be between 3 weeks ago and 2 years ago
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Approximately, how old was the injured party during the time of the sexual exploitation? *
              </label>
              <input
                type="number"
                min="1"
                max="17"
                value={enrollmentData.injuredPartyAgeAtExploitation || ''}
                readOnly
                className={`w-full px-3 py-2 border rounded-md bg-gray-100 cursor-not-allowed ${
                  errors.injuredPartyAgeAtExploitation ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Auto-calculated from date of birth and incident date"
              />
              {errors.injuredPartyAgeAtExploitation && (
                <p className="mt-1 text-sm text-red-600">{errors.injuredPartyAgeAtExploitation}</p>
              )}
              
              {/* DQ Warning when age 18 or older is calculated */}
              {enrollmentData.injuredPartyAgeAtExploitation >= 18 && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ DISQUALIFICATION WARNING: Injured party must have been under 18 at time of exploitation.
                  </p>
                </div>
              )}
              
              <p className="mt-1 text-xs text-gray-500">
                This field is automatically calculated from the date of birth and incident date
              </p>
            </div>
          </div>

          {/* Roblox Account Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Did the injured party have their own Roblox account through which the incident occurred? *
              </label>
              <select
                value={enrollmentData.hasOwnRobloxAccount === undefined ? '' : enrollmentData.hasOwnRobloxAccount ? 'Yes' : 'No'}
                onChange={(e) => updateField('hasOwnRobloxAccount', e.target.value === 'Yes')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.hasOwnRobloxAccount ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.hasOwnRobloxAccount && (
                <p className="mt-1 text-sm text-red-600">{errors.hasOwnRobloxAccount}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Does the injured party know their Roblox username? *
              </label>
              <select
                value={enrollmentData.knowsRobloxUsername === undefined ? '' : enrollmentData.knowsRobloxUsername ? 'Yes' : 'No'}
                onChange={(e) => updateField('knowsRobloxUsername', e.target.value === 'Yes')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.knowsRobloxUsername ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.knowsRobloxUsername && (
                <p className="mt-1 text-sm text-red-600">{errors.knowsRobloxUsername}</p>
              )}
            </div>

            {/* Conditional Roblox Username field */}
            {enrollmentData.knowsRobloxUsername === true && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Roblox User Name *
                </label>
                <input
                  type="text"
                  value={enrollmentData.robloxUsername || ''}
                  onChange={(e) => updateField('robloxUsername', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.robloxUsername ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter Roblox username"
                />
                {errors.robloxUsername && (
                  <p className="mt-1 text-sm text-red-600">{errors.robloxUsername}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Does the injured party have access to their Roblox account? *
              </label>
              <select
                value={enrollmentData.hasRobloxAccountAccess === undefined ? '' : enrollmentData.hasRobloxAccountAccess ? 'Yes' : 'No'}
                onChange={(e) => updateField('hasRobloxAccountAccess', e.target.value === 'Yes')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.hasRobloxAccountAccess ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.hasRobloxAccountAccess && (
                <p className="mt-1 text-sm text-red-600">{errors.hasRobloxAccountAccess}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Did the incident occur through another minor's Roblox account that the injured party was using or involved with? *
              </label>
              <select
                value={enrollmentData.incidentThroughAnotherAccount === undefined ? '' : enrollmentData.incidentThroughAnotherAccount ? 'Yes' : 'No'}
                onChange={(e) => updateField('incidentThroughAnotherAccount', e.target.value === 'Yes')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.incidentThroughAnotherAccount ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.incidentThroughAnotherAccount && (
                <p className="mt-1 text-sm text-red-600">{errors.incidentThroughAnotherAccount}</p>
              )}
            </div>

            {/* Conditional field for exploitation cause when incident did NOT occur through another account */}
            {enrollmentData.incidentThroughAnotherAccount === false && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What caused the injured party to suffer sexual exploitation? *
                </label>
                <textarea
                  value={enrollmentData.exploitationCause || ''}
                  onChange={(e) => updateField('exploitationCause', e.target.value)}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.exploitationCause ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Describe what caused the sexual exploitation..."
                />
                {errors.exploitationCause && (
                  <p className="mt-1 text-sm text-red-600">{errors.exploitationCause}</p>
                )}
              </div>
            )}
          </div>

          {/* Communication Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Was there any sexual or suggestive communication through Roblox involving any of the following (Check all that Apply) *
            </label>
            <div className="space-y-3">
              {communicationTypes.map((type) => (
                <label key={type} className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={(enrollmentData.communicationTypes || []).includes(type)}
                    onChange={(e) => handleCheckboxChange('communicationTypes', type, e.target.checked)}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{type}</span>
                </label>
              ))}
              {errors.communicationTypes && (
                <p className="mt-2 text-sm text-red-600">{errors.communicationTypes}</p>
              )}
              
              {/* DQ Warning when "None of the Above" is selected */}
              {(enrollmentData.communicationTypes || []).some((type: string) => type.toLowerCase().includes('none of the above')) && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ DISQUALIFICATION WARNING: Selecting "None of the Above" will disqualify this enrollment.
                  </p>
                </div>
              )}
              
              <p className="mt-2 text-xs text-red-600">
                DQ - If NONE ABOVE
              </p>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Please describe what led to sexual exploitation? *
            </label>
            <textarea
              value={enrollmentData.exploitationDescription || ''}
              onChange={(e) => updateField('exploitationDescription', e.target.value)}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.exploitationDescription ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Provide a detailed description of what led to the sexual exploitation"
            />
            {errors.exploitationDescription && (
              <p className="mt-1 text-sm text-red-600">{errors.exploitationDescription}</p>
            )}
          </div>

          {/* Perpetrator Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Did the injured party initially meet the adult perpetrator on Roblox *
              </label>
              <select
                value={enrollmentData.metPerpetratorOnRoblox === undefined ? '' : enrollmentData.metPerpetratorOnRoblox ? 'Yes' : 'No'}
                onChange={(e) => updateField('metPerpetratorOnRoblox', e.target.value === 'Yes')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.metPerpetratorOnRoblox ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.metPerpetratorOnRoblox && (
                <p className="mt-1 text-sm text-red-600">{errors.metPerpetratorOnRoblox}</p>
              )}
              
              {/* DQ Warning when "No" is selected */}
              {enrollmentData.metPerpetratorOnRoblox === false && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ DISQUALIFICATION WARNING: The injured party must have initially met the adult perpetrator on Roblox.
                  </p>
                </div>
              )}
              
              <p className="mt-1 text-xs text-red-600">
                IF No - DQ
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Did the injured party meet the adult perpetrator in person? *
              </label>
              <select
                value={enrollmentData.metPerpetratorInPerson === undefined ? '' : enrollmentData.metPerpetratorInPerson ? 'Yes' : 'No'}
                onChange={(e) => updateField('metPerpetratorInPerson', e.target.value === 'Yes')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.metPerpetratorInPerson ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.metPerpetratorInPerson && (
                <p className="mt-1 text-sm text-red-600">{errors.metPerpetratorInPerson}</p>
              )}
            </div>

            {/* Conditional field for sexual assault when they met in person */}
            {enrollmentData.metPerpetratorInPerson === true && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Was the injured party sexually assaulted when they met the adult perpetrator in person? *
                </label>
                <textarea
                  value={enrollmentData.sexuallyAssaultedInPerson || ''}
                  onChange={(e) => updateField('sexuallyAssaultedInPerson', e.target.value)}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.sexuallyAssaultedInPerson ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Describe what happened when they met in person..."
                />
                {errors.sexuallyAssaultedInPerson && (
                  <p className="mt-1 text-sm text-red-600">{errors.sexuallyAssaultedInPerson}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Was a police report filed? *
              </label>
              <select
                value={enrollmentData.policeReportFiled === undefined ? '' : enrollmentData.policeReportFiled ? 'Yes' : 'No'}
                onChange={(e) => updateField('policeReportFiled', e.target.value === 'Yes')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.policeReportFiled ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.policeReportFiled && (
                <p className="mt-1 text-sm text-red-600">{errors.policeReportFiled}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Validation Error Display */}
      <ValidationErrorDisplay
        errors={getValidationErrors()}
        title="Please resolve the following issues to continue:"
      />
    </StageWrapper>
  )
}